// Legacy-route redirect wiring (Top-25 #11, 02 §A "universal redirect
// contract", AC-REDIR-1..5). The register itself lives in
// packages/shared/src/routeMap.js (LEGACY_ROUTE_ALIASES); this module renders
// one router entry per register row that:
//   - re-substitutes path params into the target (`:id` placeholders),
//   - merges the target's fixed query (e.g. `?view=capacity`) with the
//     incoming query — fixed target params win on conflict, every other
//     incoming param is preserved,
//   - issues a replace navigation (permanent semantics — Back is never
//     trapped on the alias), and
//   - passes through the same RBAC guard as its target, so a user denied at
//     the target is denied identically via the alias (RBAC parity,
//     AC-REDIR-3).
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { rbac, routeMap } from '@dha-nexus/shared';
import { RequireRoute } from './RouteGuards.jsx';

// Guard-parity map: alias -> the ROUTE_PERMISSIONS key its TARGET route is
// guarded with in App.jsx. Kept explicit rather than derived because some
// canonical routes are guarded under a parent key (e.g. `/meetings/:id`
// renders behind the '/meetings' permission). LegacyRedirects.test.jsx
// asserts this map covers the shared register exactly 1:1.
export const LEGACY_ALIAS_GUARD_KEYS = Object.freeze({
  '/project-factory/review': '/project-factory',
  '/analytics/loes': '/loe-dashboard',
  '/analytics/resources': '/resources',
  '/admin/report-templates': '/admin/config/report-registry',
  '/exports': '/products',
  '/work-items/:id/log-time': '/work-items/:id',
  '/meetings/:id/convert-to-time': '/meetings',
  '/resources/scheduler': '/resources',
  '/resources/utilization': '/resources',
  '/analytics/mission-impact': '/executive',
  '/admin/data-integrations': '/admin/data',
});

/**
 * Client redirect for one register row. `target` may carry a fixed query
 * string; `:param` tokens in the target path are re-substituted from the
 * matched alias params.
 */
export function LegacyRedirect({ target }) {
  const params = useParams();
  const location = useLocation();

  const [targetPath, fixedQuery = ''] = target.split('?');
  const pathname = targetPath.replace(/:([A-Za-z0-9_]+)/g, (token, name) =>
    params[name] != null ? params[name] : token,
  );

  const merged = new URLSearchParams(location.search);
  for (const [key, value] of new URLSearchParams(fixedQuery)) {
    merged.set(key, value); // fixed target params win on conflict
  }
  const search = merged.toString();

  return (
    <Navigate
      replace
      to={{ pathname, search: search ? `?${search}` : '', hash: location.hash || '' }}
    />
  );
}

/**
 * One <Route> per LEGACY_ROUTE_ALIASES row, each wrapped in the same
 * RequireRoute guard as its canonical target. Rendered inside the AppShell
 * layout route in App.jsx so redirects share the target's auth + shell
 * context.
 */
export function renderLegacyRedirectRoutes() {
  return Object.entries(routeMap.LEGACY_ROUTE_ALIASES).map(([alias, target]) => (
    <Route
      key={alias}
      path={alias}
      element={
        <RequireRoute roles={rbac.ROUTE_PERMISSIONS[LEGACY_ALIAS_GUARD_KEYS[alias]] || []}>
          <LegacyRedirect target={target} />
        </RequireRoute>
      }
    />
  ));
}
